import React, { useState, useEffect } from 'react';
import { 
  X, DollarSign, Calendar, Clock, FileText, 
  User, BookOpen, AlertCircle, CheckCircle,
  Calculator, Target, Award, GraduationCap,
  List, CreditCard, Plus, Trash2
} from 'lucide-react';
import jsPDF from 'jspdf'; 

const NuevaCotizacion = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  isLoading, 
  tesisData 
}) => {
  const [formData, setFormData] = useState({
    precio: '',
    metodoPago: 'contado', // contado, avances
    diasValidez: '15',
    numeroAvances: '3',
    clausulas: '',
    fechaEntrega: '',
    fechasPago: [], // Array de fechas para pago por avances
    descuentoPorcentaje: '' // Campo de descuento en %
  });

  const [errores, setErrores] = useState({});

  // Resetear form cuando se abre/cierra el modal
  useEffect(() => {
    if (isOpen) {
      // Calcular fecha mínima (mañana)
      const mañana = new Date();
      mañana.setDate(mañana.getDate() + 1);
      const fechaMinima = mañana.toISOString().split('T')[0];

      setFormData({
        precio: '',
        metodoPago: 'contado',
        diasValidez: '15',
        numeroAvances: '3',
        clausulas: '',
        fechaEntrega: fechaMinima,
        fechasPago: [],
        descuentoPorcentaje: ''
      });
      setErrores({});
    }
  }, [isOpen]);

  // Actualizar fechas de pago cuando cambia el número de avances
  useEffect(() => {
    if (formData.metodoPago === 'avances' && formData.numeroAvances) {
      const numAvances = parseInt(formData.numeroAvances);
      const fechasActuales = [...formData.fechasPago];
      
      // Ajustar el array de fechas según el número de avances
      if (fechasActuales.length < numAvances) {
        // Agregar fechas vacías
        for (let i = fechasActuales.length; i < numAvances; i++) {
          fechasActuales.push('');
        }
      } else if (fechasActuales.length > numAvances) {
        // Quitar fechas sobrantes
        fechasActuales.splice(numAvances);
      }
      
      setFormData(prev => ({
        ...prev,
        fechasPago: fechasActuales
      }));
    }
  }, [formData.numeroAvances, formData.metodoPago]);

  // Obtener información del nivel de tesis
  const obtenerInfoNivel = (nivel) => {
    const nivelUpper = nivel?.toUpperCase() || 'PREGRADO';
    
    const mapeoNiveles = {
      'PREGRADO': { 
        icon: <GraduationCap size={20} />, 
        color: 'from-blue-500 to-blue-600',
        bgColor: 'bg-blue-50',
        textColor: 'text-blue-700',
        label: 'Pregrado' 
      },
      'POSTGRADO': { 
        icon: <Award size={20} />, 
        color: 'from-green-500 to-green-600',
        bgColor: 'bg-green-50',
        textColor: 'text-green-700',
        label: 'Postgrado' 
      },
      'MAESTRIA': { 
        icon: <Target size={20} />, 
        color: 'from-yellow-500 to-orange-500',
        bgColor: 'bg-orange-50',
        textColor: 'text-orange-700',
        label: 'Maestría' 
      },
      'DOCTORADO': { 
        icon: <BookOpen size={20} />, 
        color: 'from-red-500 to-red-600',
        bgColor: 'bg-red-50',
        textColor: 'text-red-700',
        label: 'Doctorado' 
      }
    };

    return mapeoNiveles[nivelUpper] || mapeoNiveles['PREGRADO'];
  };

  // Validar formulario
  const validarFormulario = () => {
    const nuevosErrores = {};

    if (!formData.precio || formData.precio <= 0) {
      nuevosErrores.precio = 'El precio es obligatorio y debe ser mayor a 0';
    }

    if (!formData.fechaEntrega) {
      nuevosErrores.fechaEntrega = 'La fecha de entrega es obligatoria';
    } else {
      const fechaSeleccionada = new Date(formData.fechaEntrega);
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      if (fechaSeleccionada <= hoy) {
        nuevosErrores.fechaEntrega = 'La fecha de entrega debe ser posterior a hoy';
      }
    }

    // Validar fechas de pago por avances
    if (formData.metodoPago === 'avances') {
      const avances = parseInt(formData.numeroAvances);
      if (!avances || avances <= 0 || avances > 10) {
        nuevosErrores.numeroAvances = 'El número de avances debe estar entre 1 y 10';
      }

      // Validar que todas las fechas de pago estén completas
      for (let i = 0; i < formData.fechasPago.length; i++) {
        if (!formData.fechasPago[i]) {
          nuevosErrores[`fechaPago${i}`] = `La fecha del avance ${i + 1} es obligatoria`;
        } else {
          const fechaPago = new Date(formData.fechasPago[i]);
          const hoy = new Date();
          hoy.setHours(0, 0, 0, 0);
          if (fechaPago <= hoy) {
            nuevosErrores[`fechaPago${i}`] = `La fecha del avance ${i + 1} debe ser posterior a hoy`;
          }
        }
      }
    }

    const validez = parseFloat(formData.diasValidez);
    if (!validez || validez <= 0) {
      nuevosErrores.diasValidez = 'Los días de validez deben ser mayor a 0';
    }

    if (!formData.clausulas.trim()) {
      nuevosErrores.clausulas = 'Las cláusulas son obligatorias';
    }

    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  // Manejar cambios en el formulario
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Limpiar error específico cuando el usuario corrige
    if (errores[name]) {
      setErrores(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Manejar cambio en fechas de pago
  const handleFechaPagoChange = (index, fecha) => {
    const nuevasFechas = [...formData.fechasPago];
    nuevasFechas[index] = fecha;
    setFormData(prev => ({
      ...prev,
      fechasPago: nuevasFechas
    }));

    // Limpiar error específico
    if (errores[`fechaPago${index}`]) {
      setErrores(prev => ({
        ...prev,
        [`fechaPago${index}`]: ''
      }));
    }
  };

  const generarCotizacion = async (cotizacionData) => {

    // --- 1. HELPERS Y CONFIGURACIÓN INTERNA ---
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    let yPos = margin;

    // Helper para formatear moneda (Pesos Colombianos)
    const formatCurrency = (num) => {
      const number = parseFloat(num);
      if (isNaN(number)) return '$0';
      return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(number);
    };

    // Helper para formatear fechas de manera legible
    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString + 'T00:00:00'); // Evita errores de zona horaria
      return date.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    // Helper para garantizar que el valor sea siempre un string seguro
    const safeString = (value, fallback = '-') => String(value || fallback);


    // --- 2. HEADER DEL DOCUMENTO ---
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 58, 138); // Azul oscuro
    doc.text('COTIZACIÓN DE SERVICIOS ACADÉMICOS', margin, yPos);
    yPos += 10;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139); // Gris
    doc.text(`Proyecto ID: #${safeString(tesisData.id)}`, margin, yPos);
    doc.text(`Fecha de Emisión: ${formatDate(new Date().toISOString())}`, pageWidth - margin, yPos, { align: 'right' });
    yPos += 10;


    // --- 3. PÁRRAFO DE INTRODUCCIÓN ---
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const introText = `Estimado(a) ${safeString(tesisData.cliente, 'Cliente')},\n\nNos complace presentar la siguiente cotización detallada para los servicios de asesoría y desarrollo académico, preparada conforme a los requerimientos de su proyecto.`;
    const introLines = doc.splitTextToSize(introText, pageWidth - margin * 2);
    doc.text(introLines, margin, yPos);
    yPos += (introLines.length * 5) + 10;


    // --- 4. SECCIÓN DE DOS COLUMNAS (CAMPOS ELIMINADOS) ---
    const columnWidth = (pageWidth - margin * 3) / 2;
    const leftColX = margin;
    const rightColX = margin * 2 + columnWidth;
    let yPosLeft = yPos;

    const addDetailLine = (y, colX, label, value) => {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(51, 65, 85);
        doc.text(label, colX, y);
        
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 116, 139);
        const textLines = doc.splitTextToSize(safeString(value), columnWidth - 25);
        doc.text(textLines, colX + 40, y);
        return y + (textLines.length * 4) + 3; // Retorna la nueva posición Y
    };
    
    // Columna Izquierda: Información del Proyecto
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 58, 138);
    doc.text('Detalles del Proyecto', leftColX, yPosLeft);
    yPosLeft += 8;
    yPosLeft = addDetailLine(yPosLeft, leftColX, 'Título:', tesisData.titulo);
    yPosLeft = addDetailLine(yPosLeft, leftColX, 'Nivel Académico:', tesisData.nivel);
    yPosLeft = addDetailLine(yPosLeft, leftColX, 'Estudiante:', tesisData.estudiante);

    // Columna Derecha: Detalles de la Oferta
    let yPosRight = yPos;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 58, 138);
    doc.text('Detalles de la Oferta', rightColX, yPosRight);
    yPosRight += 8;
    yPosRight = addDetailLine(yPosRight, rightColX, 'Cliente:', tesisData.cliente);
    yPosRight = addDetailLine(yPosRight, rightColX, 'Entrega Propuesta:', formatDate(cotizacionData.fechaEntrega));
    yPosRight = addDetailLine(yPosRight, rightColX, 'Validez de la Oferta:', `${cotizacionData.diasValidez} días`);

    yPos = Math.max(yPosLeft, yPosRight) + 15;


    // --- 5. RESUMEN FINANCIERO (CORREGIDO) ---
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 58, 138);
    doc.text('Resumen Financiero', margin, yPos);
    yPos += 8;
    
    doc.setDrawColor(226, 232, 240); // Línea divisoria gris claro
    doc.line(margin, yPos, pageWidth - margin, yPos); // Línea superior de la tabla
    yPos += 8;

    if (cotizacionData.metodoPago === 'contado') {
        const precioBase = cotizacionData.precio;
        const descuentoPorc = parseFloat(cotizacionData.descuentoPorcentaje) || 0;
        const montoDescuento = (precioBase * descuentoPorc) / 100;
        const precioFinal = precioBase - montoDescuento;
        doc.setFontSize(10);
        doc.text('Precio Base del Servicio', margin, yPos);
        doc.text(formatCurrency(precioBase), pageWidth - margin, yPos, { align: 'right' });
        yPos += 7;
        if (descuentoPorc > 0) {
            doc.setTextColor(22, 163, 74); // Verde para el descuento
            doc.text(`Descuento (${descuentoPorc}%)`, margin, yPos);
            doc.text(`- ${formatCurrency(montoDescuento)}`, pageWidth - margin, yPos, { align: 'right' });
            yPos += 7;
            doc.setTextColor(51, 65, 85); // Resetear color
        }
        yPos += 2;
        doc.setDrawColor(200);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 8;
        doc.setFont('helvetica', 'bold');
        doc.text('TOTAL A PAGAR (Contado)', margin, yPos);
        doc.text(formatCurrency(precioFinal), pageWidth - margin, yPos, { align: 'right' });
    } else { // Pago por Avances
        const montoPorAvance = cotizacionData.precio / cotizacionData.fechasPago.length;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Descripción', margin, yPos);
        doc.text('Fecha de Pago', pageWidth / 2, yPos, { align: 'center' });
        doc.text('Monto', pageWidth - margin, yPos, { align: 'right' });
        yPos += 7;
        doc.setFont('helvetica', 'normal');
        cotizacionData.fechasPago.forEach((fecha, index) => {
            doc.text(`Pago Avance ${index + 1}`, margin, yPos);
            doc.text(formatDate(fecha), pageWidth / 2, yPos, { align: 'center' });
            doc.text(formatCurrency(montoPorAvance), pageWidth - margin, yPos, { align: 'right' });
            yPos += 7;
        });
        yPos += 2;
        doc.setDrawColor(200);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 8;
        doc.setFont('helvetica', 'bold');
        doc.text('TOTAL DEL PROYECTO', margin, yPos);
        doc.text(formatCurrency(cotizacionData.precio), pageWidth - margin, yPos, { align: 'right' });
    }
    yPos += 8;
    doc.setDrawColor(226, 232, 240);
    doc.line(margin, yPos, pageWidth - margin, yPos); // Línea inferior de la tabla
    yPos += 15;


    // --- 6. CLÁUSULAS Y CONDICIONES (CON TEXTO POR DEFECTO MEJORADO) ---
    const defaultClauses = `1. ALCANCE: El servicio incluye las actividades descritas en la propuesta. Cualquier cambio o adición requerirá una nueva cotización.
2. PAGOS: Los pagos deben realizarse en las fechas acordadas. El incumplimiento puede generar la suspensión del servicio.
3. CONFIDENCIALIDAD: Ambas partes se comprometen a mantener la confidencialidad de la información compartida.
4. PROPIEDAD INTELECTUAL: Una vez completados todos los pagos, los derechos sobre el trabajo final serán transferidos al cliente.
5. CORRECCIONES: Se incluyen hasta dos (2) rondas de correcciones sobre el trabajo entregado, basadas en los lineamientos iniciales.`;
    
    const clausulasFinales = (safeString(cotizacionData.clausulas).length > 5) ? cotizacionData.clausulas : defaultClauses;
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 58, 138);
    doc.text('Cláusulas y Condiciones', margin, yPos);
    yPos += 7;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    const clausulasLines = doc.splitTextToSize(clausulasFinales, pageWidth - margin * 2);
    if (yPos + (clausulasLines.length * 4) > pageHeight - 30) {
        doc.addPage();
        yPos = margin;
    }
    doc.text(clausulasLines, margin, yPos);
    yPos += (clausulasLines.length * 4) + 15;


    // --- 7. CIERRE Y FOOTER ---
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Agradecemos su tiempo y consideración. Quedamos a su disposición para cualquier consulta.', margin, yPos);
    
    const footerY = pageHeight - 20;
    doc.setDrawColor(226, 232, 240);
    doc.line(margin, footerY, pageWidth - margin, footerY);
    yPos = footerY + 8;
    doc.setFontSize(8);
    doc.setTextColor(156, 163, 175);
    doc.text('Asesorías Académicas S.A.S. | Email: contacto@asesorias.com | Tel: (57) 300-123-4567', margin, yPos);
    doc.text(`Página 1 de 1`, pageWidth - margin, yPos, { align: 'right' });


    // --- 8. GUARDAR EL DOCUMENTO ---
    const fileName = `Cotizacion-${safeString(tesisData.id)}-${safeString(tesisData.cliente, 'Proyecto')}.pdf`;
    doc.save(fileName);
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validarFormulario()) {
      return;
    }

    try {
      const cotizacionData = {
        ...formData,
        precio: parseFloat(formData.precio),
        diasValidez: parseInt(formData.diasValidez),
        numeroAvances: parseInt(formData.numeroAvances),
        fechaCreacion: new Date(),
        tesisId: tesisData?.id
      };

      await generarCotizacion(cotizacionData);
    } catch (error) {
      console.error('Error al enviar cotización:', error);
    }
  };

  // Calcular días hasta la entrega
  const calcularDiasHastaEntrega = () => {
    if (!formData.fechaEntrega) return '';
    
    const fechaEntrega = new Date(formData.fechaEntrega);
    const hoy = new Date();
    const diferencia = Math.ceil((fechaEntrega - hoy) / (1000 * 60 * 60 * 24));
    
    if (diferencia <= 0) return 'Fecha inválida';
    if (diferencia === 1) return '1 día';
    if (diferencia < 7) return `${diferencia} días`;
    if (diferencia < 30) return `${diferencia} días (${Math.ceil(diferencia / 7)} semanas)`;
    return `${diferencia} días (${Math.ceil(diferencia / 30)} meses)`;
  };

  if (!isOpen) return null;

  const infoNivel = obtenerInfoNivel(tesisData?.nivel);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                <DollarSign size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-left text-white">Nueva Cotización</h2>
                <p className="text-blue-100 text-left">Crear presupuesto para proyecto de tesis</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 p-2 rounded-lg transition-colors"
              disabled={isLoading}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="flex h-[calc(90vh-120px)]">
          {/* Panel izquierdo - Información de la tesis */}
          <div className="w-2/5 bg-gray-50 p-6 border-r border-gray-200 overflow-y-auto">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText size={18} />
              Información del Proyecto
            </h3>
            
            {tesisData && (
              <div className="space-y-4">
                {/* ID y Nivel */}
                <div className={`${infoNivel.bgColor} p-4 rounded-lg border`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`p-2 rounded-lg bg-gradient-to-br ${infoNivel.color} text-white`}>
                      {infoNivel.icon}
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">#{tesisData.id}</div>
                      <div className={`text-sm ${infoNivel.textColor} font-medium`}>
                        {infoNivel.label}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Servicio */}
                <div>
                  <label className="text-sm font-medium text-gray-600">Servicio</label>
                  <p className="text-gray-900 font-semibold mt-1 text-lg">
                    {tesisData.servicio || 'TESIS'}
                  </p>
                </div>

                {/* Título */}
                <div>
                  <label className="text-sm font-medium text-gray-600">Título del Proyecto</label>
                  <p className="text-gray-900 font-medium mt-1">
                    {tesisData.titulo || tesisData.resumen || 'Proyecto de Tesis'}
                  </p>
                </div>

                {/* Materia */}
                <div>
                  <label className="text-sm font-medium text-gray-600">Materia</label>
                  <p className="text-gray-900 mt-1 flex items-center gap-2">
                    <BookOpen size={16} className="text-blue-600" />
                    {tesisData.materia}
                  </p>
                </div>

                {/* Estudiante */}
                {tesisData.estudiante && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Estudiante</label>
                    <p className="text-gray-900 mt-1 flex items-center gap-2">
                      <User size={16} className="text-green-600" />
                      {tesisData.estudiante}
                    </p>
                  </div>
                )}

                {/* Cliente */}
                {tesisData.cliente && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Cliente</label>
                    <p className="text-gray-900 mt-1">{tesisData.cliente}</p>
                  </div>
                )}

                {/* Fecha de entrega original */}
                <div>
                  <label className="text-sm font-medium text-gray-600">Fecha de Entrega Requerida</label>
                  <p className="text-gray-900 mt-1 flex items-center gap-2">
                    <Calendar size={16} className="text-red-600" />
                    {new Date(tesisData.fechaentrega).toLocaleDateString('es-ES', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>

                {/* Descripción */}
                {tesisData.resumen && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Descripción</label>
                    <div className="text-sm text-gray-700 mt-1 bg-white p-3 rounded-lg border max-h-32 overflow-y-auto">
                      {tesisData.resumen}
                    </div>
                  </div>
                )}

                {/* Estado actual */}
                <div>
                  <label className="text-sm font-medium text-gray-600">Estado Actual</label>
                  <p className="text-gray-900 mt-1">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {tesisData.Estado || 'DISPONIBLE'}
                    </span>
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Panel derecho - Formulario de cotización */}
          <div className="flex-1 p-6 overflow-y-auto">
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Precio */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Precio Total *
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="number"
                    name="precio"
                    value={formData.precio}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-lg font-semibold ${errores.precio ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    disabled={isLoading}
                  />
                </div>
                {errores.precio && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errores.precio}
                  </p>
                )}
              </div>

              {/* Descuento */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Descuento (%)
                </label>
                <div className="relative max-w-xs">
                  <input
                    type="number"
                    name="descuentoPorcentaje"
                    value={formData.descuentoPorcentaje}
                    onChange={handleChange}
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-lg font-semibold"
                    placeholder="0"
                    min="0"
                    max="100"
                    step="0.1"
                    disabled={isLoading}
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">%</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">Descuento aplicable sobre el precio base</p>
              </div>

              {/* Método de pago */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Método de Pago *
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${formData.metodoPago === 'contado' ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:bg-gray-50'}`}>
                    <input
                      type="radio"
                      name="metodoPago"
                      value="contado"
                      checked={formData.metodoPago === 'contado'}
                      onChange={handleChange}
                      className="text-blue-600 focus:ring-blue-500"
                      disabled={isLoading}
                    />
                    <div className="ml-3">
                      <div className="font-medium text-gray-900 flex items-center gap-2">
                        <CreditCard size={16} />
                        Pago de Contado
                      </div>
                      <div className="text-sm text-gray-600">Pago único al completar el trabajo</div>
                    </div>
                  </label>

                  <label className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${formData.metodoPago === 'avances' ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:bg-gray-50'}`}>
                    <input
                      type="radio"
                      name="metodoPago"
                      value="avances"
                      checked={formData.metodoPago === 'avances'}
                      onChange={handleChange}
                      className="text-blue-600 focus:ring-blue-500"
                      disabled={isLoading}
                    />
                    <div className="ml-3">
                      <div className="font-medium text-gray-900 flex items-center gap-2">
                        <List size={16} />
                        Pago por Avances
                      </div>
                      <div className="text-sm text-gray-600">Pagos programados por cada avance</div>
                    </div>
                  </label>
                </div>

                {/* Configuración de avances */}
                {formData.metodoPago === 'avances' && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Número de Avances *
                      </label>
                      <div className="relative max-w-xs">
                        <List className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                          type="number"
                          name="numeroAvances"
                          value={formData.numeroAvances}
                          onChange={handleChange}
                          className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${errores.numeroAvances ? 'border-red-500' : 'border-gray-300'}`}
                          placeholder="3"
                          min="1"
                          max="10"
                          disabled={isLoading}
                        />
                      </div>
                      {errores.numeroAvances && (
                        <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                          <AlertCircle size={14} />
                          {errores.numeroAvances}
                        </p>
                      )}
                    </div>

                    {/* Fechas de pago por avance */}
                    {formData.fechasPago.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Fechas de Pago por Avance
                        </label>
                        <div className="space-y-3">
                          {formData.fechasPago.map((fecha, index) => (
                            <div key={index} className="flex items-center gap-3 bg-white p-3 rounded-lg border">
                              <div className="flex-shrink-0 w-20">
                                <span className="text-sm font-medium text-gray-700">
                                  Avance {index + 1}:
                                </span>
                              </div>
                              <div className="flex-1">
                                <input
                                  type="date"
                                  value={fecha}
                                  onChange={(e) => handleFechaPagoChange(index, e.target.value)}
                                  min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${errores[`fechaPago${index}`] ? 'border-red-500' : 'border-gray-300'}`}
                                  disabled={isLoading}
                                />
                                {errores[`fechaPago${index}`] && (
                                  <p className="text-red-500 text-xs mt-1">
                                    {errores[`fechaPago${index}`]}
                                  </p>
                                )}
                              </div>
                              {formData.precio && (
                                <div className="flex-shrink-0 text-sm text-gray-600 font-medium">
                                  ${(parseFloat(formData.precio) / formData.fechasPago.length).toFixed(2)}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Fecha de entrega */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Fecha de Entrega *
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="date"
                    name="fechaEntrega"
                    value={formData.fechaEntrega}
                    onChange={handleChange}
                    min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${errores.fechaEntrega ? 'border-red-500' : 'border-gray-300'}`}
                    disabled={isLoading}
                  />
                </div>
                {formData.fechaEntrega && !errores.fechaEntrega && (
                  <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                    <CheckCircle size={14} />
                    Tiempo de trabajo: {calcularDiasHastaEntrega()}
                  </p>
                )}
                {errores.fechaEntrega && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errores.fechaEntrega}
                  </p>
                )}
              </div>

              {/* Días de validez */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Días de Validez *
                </label>
                <div className="relative max-w-xs">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="number"
                    name="diasValidez"
                    value={formData.diasValidez}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${errores.diasValidez ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="15"
                    min="1"
                    max="365"
                    disabled={isLoading}
                  />
                </div>
                {errores.diasValidez && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errores.diasValidez}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">Días que tendrá validez esta cotización</p>
              </div>

              {/* Cláusulas */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Cláusulas y Condiciones *
                </label>
                <textarea
                  name="clausulas"
                  value={formData.clausulas}
                  onChange={handleChange}
                  rows={6}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${errores.clausulas ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Ejemplo:
• El trabajo incluye desarrollo completo de la tesis según lineamientos académicos
• Se realizarán reuniones de seguimiento semanales
• El cliente debe proporcionar acceso a recursos bibliográficos necesarios
• Las correcciones menores están incluidas en el precio
• Cambios mayores al alcance inicial tendrán costo adicional
• El pago debe realizarse según el método acordado..."
                  disabled={isLoading}
                />
                {errores.clausulas && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errores.clausulas}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">Términos y condiciones que regirán el trabajo</p>
              </div>

              {/* Botones */}
              <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  disabled={isLoading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl font-medium flex items-center gap-2"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Creando Cotización...
                    </>
                  ) : (
                    <>
                      <Calculator size={16} />
                      Crear Cotización
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NuevaCotizacion;